/* Product companion upsell + accordion relocation
 *
 * Two responsibilities:
 *
 * 1. Companion upsell — if the merchant didn't curate companion products on
 *    the product metafield, fetch Shopify's complementary recommendations and
 *    populate the upsell list with up to N items.
 *
 * 2. Accordion relocation — the merchant asked to swap the position of the
 *    accordion ("dropdown bars") and the companion upsell. The accordion lives
 *    on the right column inside .product__info; we move all `.about` blocks
 *    out of there and into a new container that sits BELOW the product image
 *    in the left column (.product__main).
 *
 *    On mobile both columns stack, so the visual ordering is preserved by
 *    placing the new container after the media in DOM order.
 */
(function () {
  'use strict'

  // ---- 1. Companion upsell ----

  function renderCompanionItems(listEl, products, limit, addLabel) {
    const slice = products.slice(0, limit)
    listEl.innerHTML = ''
    slice.forEach(function (p) {
      const li = document.createElement('li')
      li.className = 'product__companion-upsell__item'

      const subtitle = (p.metafields && p.metafields.custom && p.metafields.custom.product_subtitle) || ''

      li.innerHTML =
        '<a class="product__companion-upsell__media" href="' + p.url + '">' +
        (p.featured_image ? '<img loading="lazy" width="64" height="64" src="' + p.featured_image + '&width=128&height=128&crop=center" alt="' + (p.title || '').replace(/"/g, '&quot;') + '">' : '') +
        '</a>' +
        '<div class="product__companion-upsell__body">' +
        '<a class="product__companion-upsell__title" href="' + p.url + '">' + (p.title || '') + '</a>' +
        (subtitle ? '<p class="product__companion-upsell__subtitle">' + subtitle + '</p>' : '') +
        '<p class="product__companion-upsell__price">' + (p.price ? formatMoney(p.price) : '') + '</p>' +
        '</div>' +
        '<form method="post" action="/cart/add" class="product__companion-upsell__form">' +
        '<input type="hidden" name="id" value="' + (p.variants && p.variants[0] ? p.variants[0].id : '') + '">' +
        '<input type="hidden" name="quantity" value="1">' +
        '<button type="submit" class="product__companion-upsell__add button button--secondary focus-inset">' + (addLabel || 'Add to cart') + '</button>' +
        '</form>'

      listEl.appendChild(li)
    })
  }

  function formatMoney(cents) {
    // Shopify Markets handles currency formatting via the body Money script.
    // Fallback: cents → string with 2 decimals.
    if (window.Shopify && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, '{{amount}}')
    }
    return (cents / 100).toFixed(2)
  }

  function loadCompanions() {
    document
      .querySelectorAll('.product__companion-upsell[data-product-id]')
      .forEach(function (root) {
        const listEl = root.querySelector('[data-companion-list]')
        if (!listEl) return
        // If curated items already rendered server-side, leave them
        // (and the snippet has already set display:block for that case).
        if (listEl.children.length > 0) {
          root.style.display = ''
          return
        }

        const productId = root.dataset.productId
        const limit = parseInt(root.dataset.limit, 10) || 3
        const addLabel = root.dataset.companionAdd || 'Add to cart'
        const url =
          '/recommendations/products.json?product_id=' +
          encodeURIComponent(productId) +
          '&limit=' +
          limit +
          '&intent=complementary'

        fetch(url, { headers: { Accept: 'application/json' } })
          .then(function (r) {
            return r.ok ? r.json() : null
          })
          .then(function (data) {
            if (!data || !data.products || !data.products.length) {
              // Already hidden by snippet (style="display:none") so no
              // visible change here — just confirm it stays hidden.
              root.style.display = 'none'
              return
            }
            renderCompanionItems(listEl, data.products, limit, addLabel)
            // Reveal only after items are in the DOM, eliminating the
            // flash of an empty-but-visible upsell section.
            root.style.display = 'block'
          })
          .catch(function () {
            root.style.display = 'none'
          })
      })
  }

  // ---- 2. Accordion relocation ----

  function relocateAccordion() {
    // Desktop-only: on mobile (<990px) the image column and info column stack
    // vertically — moving accordions into .product__main would place them
    // BETWEEN the image and the product title (above the ATC), which is the
    // wrong place for them. On desktop the columns sit side-by-side and the
    // relocation puts accordions under the left-column image (welleco-style).
    if (window.matchMedia('(max-width: 989px)').matches) return

    // The theme uses .product__info-container for the right column; older
    // Dawn forks called it .product__info. Try both.
    const info =
      document.querySelector('.product__info-container') ||
      document.querySelector('.product__info')
    const main = document.querySelector('.product__main')
    if (!info || !main) return
    if (main.dataset.accordionMoved === '1') return

    const accordions = info.querySelectorAll('.about')
    if (!accordions.length) return

    // Create a container appended after the media inside .product__main, so on
    // desktop the accordion sits directly below the image (left column).
    let container = main.querySelector('.product__main-accordion-host')
    if (!container) {
      container = document.createElement('div')
      container.className = 'product__main-accordion-host'
      main.appendChild(container)
    }

    accordions.forEach(function (el) {
      container.appendChild(el)
    })

    main.dataset.accordionMoved = '1'
  }

  // ---- 3. Cart drawer integration for companion ATC ----
  //
  // The companion upsell rows render a standard <form action="/cart/add"> per
  // item. Without JS the form would POST and navigate the customer to the
  // /cart page — losing their place on the PDP. Intercept the submit so we:
  //   1. Add the line via the AJAX /cart/add.js endpoint
  //   2. Request the cart-drawer + cart-icon-bubble sections so the drawer
  //      gets fresh HTML without a separate cart fetch round-trip
  //   3. Hand the parsed response to <cart-drawer>.renderContents which
  //      re-renders the drawer and calls .open() automatically
  let companionAtcWired = false
  function setupCompanionAtc() {
    if (companionAtcWired) return
    companionAtcWired = true
    document.addEventListener('submit', function (e) {
      const form = e.target
      if (!form.classList || !form.classList.contains('product__companion-upsell__form')) return
      e.preventDefault()

      const button = form.querySelector('button[type="submit"]')
      if (button) {
        button.disabled = true
        button.classList.add('loading')
      }

      const cartDrawer = document.querySelector('cart-drawer')
      const formData = new FormData(form)
      formData.append('sections', 'cart-drawer,cart-icon-bubble')
      formData.append('sections_url', window.location.pathname)

      fetch(window.routes ? window.routes.cart_add_url + '.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      })
        .then(function (r) { return r.json() })
        .then(function (response) {
          if (response.status) {
            // Shopify returned an error (e.g. variant not available)
            console.warn('[companion-upsell] add failed:', response.description || response.message)
            return
          }

          // Notify any listeners (analytics, cart counters, etc.) that the
          // basket changed — matches the event the main product form emits.
          document.dispatchEvent(new CustomEvent('variant:add', {
            detail: {
              variant: { id: formData.get('id') },
              quantity: Number(formData.get('quantity') || 1),
              formElement: form,
              sectionId: 'companion-upsell',
            },
          }))

          if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(response)
          } else {
            // Fallback if the theme's cart-drawer element isn't on the page
            // for some reason — at least don't break the customer's flow.
            window.location = (window.routes && window.routes.cart_url) || '/cart'
          }
        })
        .catch(function (err) {
          console.error('[companion-upsell] add error:', err)
        })
        .finally(function () {
          if (button) {
            button.disabled = false
            button.classList.remove('loading')
          }
        })
    })
  }

  function init() {
    loadCompanions()
    relocateAccordion()
    setupCompanionAtc()
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
  document.addEventListener('shopify:section:load', init)
})()
